import { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Tag } from 'antd';
import { sendSingleRequest } from '../../apis';
import { Dropdown, Spinner, Title } from '../../components/shared';
import FilesViewModal from '../../components/shared/pictures-wall/FilesViewModal';

interface IIssue {
  id: number;
  user: { id: number; email: string; name: string | null };
  content: string;
  images: string[] | null;
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
}

const statusColors = {
  OPEN: 'red',
  IN_PROGRESS: 'orange',
  RESOLVED: 'green',
  CLOSED: 'black',
};

const statusLabels = {
  OPEN: 'Open',
  IN_PROGRESS: 'In Progress',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
};

const statusFilterOptions = [
  { id: 'available', value: 'available', label: 'Available' },
  { id: 'closed', value: 'closed', label: 'Closed' },
];

const AdminIssuesPage = () => {
  const [issues, setIssues] = useState<IIssue[]>();
  const [loading, setLoading] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>();
  const [statusFilter, setStatusFilter] = useState<string>('available');

  const loadIssues = useCallback(async () => {
    setLoading(true);
    const res = await sendSingleRequest({}, 'GET', 'api/issues', true);
    if (res.status) {
      setIssues(res.data);
    } else {
      window.alert(res.data?.message ?? 'Something went wrong');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadIssues();
  }, [loadIssues]);

  const filteredIssues = useMemo(() => {
    if (!issues) return [];

    if (statusFilter === 'closed') {
      return issues.filter(
        issue => issue.status === 'CLOSED' || issue.status === 'RESOLVED',
      );
    } else {
      return issues.filter(
        issue => issue.status !== 'CLOSED' && issue.status !== 'RESOLVED',
      );
    }
  }, [issues, statusFilter]);

  const columns = [
    {
      title: 'ID',
      key: 'id',
      render: (x: IIssue) => <span>{x.id}</span>,
    },
    {
      title: 'User',
      key: 'user',
      render: (x: IIssue) => (
        <span>
          <div>{x.user.email}</div>
          {x.user.name && (
            <div style={{ color: 'gray', fontSize: '12px' }}>{x.user.name}</div>
          )}
        </span>
      ),
    },
    {
      title: 'Content',
      key: 'content',
      render: (x: IIssue) => (
        <span style={{ whiteSpace: 'pre-wrap' }}>{x.content}</span>
      ),
    },
    {
      title: 'Images',
      key: 'images',
      render: (x: IIssue) =>
        x.images && x.images.length > 0 ? (
          <div
            className='btn__modal'
            onClick={() => setSelectedImages(x.images ?? [])}
          >
            View ({x.images.length})
          </div>
        ) : (
          <span>-</span>
        ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (x: IIssue) => (
        <Tag color={statusColors[x.status]}>{statusLabels[x.status]}</Tag>
      ),
    },
  ];

  return (
    <div className='bg-secondary min-height'>
      <div className='container'>
        <div className='queued-page-header d-flex justify-between align-center'>
          <Title size={5} color='black' align='default' fontWeight={700}>
            Issue Reports
          </Title>
          <div style={{ width: '200px' }}>
            <Dropdown
              label=''
              options={statusFilterOptions}
              value={statusFilter}
              onChange={v => setStatusFilter(v)}
            />
          </div>
        </div>
        <div className='w-100 mt-17 pb-32'>
          {loading ? (
            <div className='d-flex justify-center align-center min-height'>
              <Spinner />
            </div>
          ) : (
            <Table
              rowKey='id'
              className='table table--isFarms'
              pagination={false}
              columns={columns}
              dataSource={filteredIssues}
            />
          )}
        </div>
      </div>
      {!!selectedImages && selectedImages.length > 0 && (
        <FilesViewModal
          visible={true}
          onClose={() => setSelectedImages(undefined)}
          files={selectedImages}
        />
      )}
    </div>
  );
};

export default AdminIssuesPage;
